/**
 * PriceCalculator Component
 * Interactive calculator for jewelry price estimation based on weight, type, and purity
 */

import React, { useState } from 'react';
import { validateWeightInput } from '../utils/inputValidation';

interface PriceCalculatorProps {
  goldRate: number | null; // Gold 24K rate per gram
  silverRate: number | null; // Silver rate per gram
}

export const PriceCalculator: React.FC<PriceCalculatorProps> = ({
  goldRate,
  silverRate,
}) => {
  const [weight, setWeight] = useState<string>('');
  const [metalType, setMetalType] = useState<'gold24k' | 'gold22k' | 'silver'>('gold24k');
  const [calculatedPrice, setCalculatedPrice] = useState<number>(0);

  const handleWeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const validated = validateWeightInput(e.target.value);
    setWeight(validated);
  };

  const handleMetalTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setMetalType(e.target.value as 'gold24k' | 'gold22k' | 'silver');
  };

  const calculatePrice = () => {
    const weightNum = parseFloat(weight) || 0;
    
    if (weightNum <= 0) {
      setCalculatedPrice(0);
      return;
    }

    if (metalType === 'silver') {
      const price = silverRate ? weightNum * silverRate : 0;
      setCalculatedPrice(price);
    } else if (metalType === 'gold24k') {
      const price = goldRate ? weightNum * goldRate : 0;
      setCalculatedPrice(price);
    } else if (metalType === 'gold22k') {
      // 22K = 91.6% of 24K price
      const price = goldRate ? weightNum * goldRate * 0.916 : 0;
      setCalculatedPrice(price);
    }
  };

  return (
    <div className="bg-gradient-to-br from-gray-950/95 to-black/95 border border-yellow-600/80 rounded-xl p-8 shadow-2xl max-w-md w-full backdrop-blur-sm">
      <h3 className="text-2xl font-serif text-white mb-8">
        Price Calculator
      </h3>

      <div className="space-y-6">
        {/* Metal Type Dropdown */}
        <div>
          <label htmlFor="metalType" className="block text-gray-500 text-[10px] font-semibold mb-3 uppercase tracking-wider">
            Select Metal Type
          </label>
          <select
            id="metalType"
            value={metalType}
            onChange={handleMetalTypeChange}
            className="w-full px-4 py-3.5 bg-gray-900/80 border border-gray-800 rounded-lg text-gray-100 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500/50 transition-all"
          >
            <option value="gold24k">Gold 24K</option>
            <option value="gold22k">Gold 22K</option>
            <option value="silver">Silver</option>
          </select>
        </div>

        {/* Weight Input */}
        <div>
          <label htmlFor="weight" className="block text-gray-500 text-[10px] font-semibold mb-3 uppercase tracking-wider">
            Weight (Grams)
          </label>
          <input
            id="weight"
            type="text"
            value={weight}
            onChange={handleWeightChange}
            placeholder="0.00"
            className="w-full px-4 py-3.5 bg-gray-900/80 border border-gray-800 rounded-lg text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500/50 transition-all"
          />
        </div>

        {/* Calculated Price Display */}
        <div className="mt-8 pt-6">
          <div className="flex justify-between items-center mb-6">
            <span className="text-gray-500 text-sm">Estimation Total</span>
            <span
              data-testid="calculated-price"
              className="text-yellow-500 text-4xl font-bold"
            >
              ₹{calculatedPrice.toFixed(2)}
            </span>
          </div>

          {/* Calculate Price Button */}
          <button
            className="w-full bg-yellow-500 hover:bg-yellow-400 text-black font-bold py-4 px-6 rounded-lg transition-all duration-200 uppercase text-sm tracking-widest shadow-lg hover:shadow-yellow-500/20"
            onClick={calculatePrice}
          >
            Calculate Price
          </button>
        </div>
      </div>
    </div>
  );
};
